import { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-hot-toast";
import {
  FiPlus, FiEye, FiTrash2, FiSearch, FiX,
  FiChevronDown, FiUploadCloud, FiList, FiLink,
} from "react-icons/fi";
import { HiX } from "react-icons/hi";
import { useFetchFormTemplateMutation } from "../../../features/admin/formTemplate/formTemplateApi";
import { useFetchVendorsQuery } from "../../../features/admin/vendor/vendorApi";
import {
  useAddFormSetupMutation,
  useDeleteFormSetupMutation,
  useFetchFormSetupMutation,
} from "../../../features/admin/formSetup/formSetupApi";

// ─── Constants ────────────────────────────────────────────────────────────────

const FORM_TYPES = [
  { id: 1,  name: "Payment" },      { id: 2,  name: "Warranty" },
  { id: 3,  name: "KYC" },          { id: 4,  name: "Login" },
  { id: 5,  name: "Registration" }, { id: 6,  name: "Profile" },
  { id: 7,  name: "Panel-AddUser" },{ id: 8,  name: "App-AddUser" },
  { id: 9,  name: "Enquiry" },      { id: 10, name: "WheelCampaign" },
  { id: 11, name: "App-Feedback" }, { id: 12, name: "Report-Qr" },
];

const TYPE_META = {
  text:     { label: "Text",     color: "bg-blue-50 text-blue-700 border-blue-200" },
  number:   { label: "Number",   color: "bg-violet-50 text-violet-700 border-violet-200" },
  date:     { label: "Date",     color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  file:     { label: "File",     color: "bg-amber-50 text-amber-700 border-amber-200" },
  camera:   { label: "Camera",   color: "bg-pink-50 text-pink-700 border-pink-200" },
  checkbox: { label: "Checkbox", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  select:   { label: "Select",   color: "bg-orange-50 text-orange-700 border-orange-200" },
  textarea: { label: "Textarea", color: "bg-teal-50 text-teal-700 border-teal-200" },
};

const CHIP_COLORS = [
  "bg-blue-50 text-blue-700 border-blue-200",
  "bg-emerald-50 text-emerald-700 border-emerald-200",
  "bg-violet-50 text-violet-700 border-violet-200",
  "bg-amber-50 text-amber-700 border-amber-200",
  "bg-pink-50 text-pink-700 border-pink-200",
  "bg-indigo-50 text-indigo-700 border-indigo-200",
  "bg-orange-50 text-orange-700 border-orange-200",
  "bg-teal-50 text-teal-700 border-teal-200",
  "bg-rose-50 text-rose-700 border-rose-200",
  "bg-cyan-50 text-cyan-700 border-cyan-200",
  "bg-lime-50 text-lime-700 border-lime-200",
  "bg-sky-50 text-sky-700 border-sky-200",
];

const getTypeName  = (id) => FORM_TYPES.find(t => t.id === Number(id))?.name || `Type ${id}`;
const getTypeColor = (id) => CHIP_COLORS[(Number(id) - 1) % CHIP_COLORS.length];

// ─── Style tokens ─────────────────────────────────────────────────────────────

const selectCls =
  "block w-full pl-3 pr-9 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all appearance-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50";

const labelCls = "block text-sm font-medium text-gray-700 mb-1";

// ─── Reusable select with chevron ─────────────────────────────────────────────

const SelectField = ({ value, onChange, disabled, children }) => (
  <div className="relative">
    <select value={value} onChange={onChange} disabled={disabled} className={selectCls}>
      {children}
    </select>
    <FiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
  </div>
);

// ─── Field preview — same style as AddForm.jsx ────────────────────────────────

const FieldPreview = ({ field }) => {
  const sharedLabel = (
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {field.label}
      {field.required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );

  if (field.type === "select") return (
    <div>
      {sharedLabel}
      <div className="relative">
        <select className="block w-full pl-3 pr-9 py-2.5 leading-5 border border-gray-300 rounded-lg bg-white text-sm text-gray-400 appearance-none cursor-not-allowed" disabled>
          <option value="">Select {field.label}…</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <FiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
      </div>
    </div>
  );

  if (field.type === "checkbox") return (
    <div className="flex items-center gap-2 pt-1">
      <input type="checkbox" disabled className="rounded border-gray-300 h-4 w-4" />
      <label className="text-sm font-medium text-gray-700">
        {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
    </div>
  );

  if (field.type === "textarea") return (
    <div>
      {sharedLabel}
      <textarea className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm resize-none text-gray-400" rows={2} disabled placeholder={`Enter ${field.label}`} />
    </div>
  );

  if (field.type === "file" || field.type === "camera") return (
    <div>
      {sharedLabel}
      <div className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl px-3 py-5 bg-gray-50">
        <FiUploadCloud className="h-5 w-5 text-gray-300" />
        <span className="text-xs text-gray-400">Click to upload</span>
      </div>
    </div>
  );

  return (
    <div>
      {sharedLabel}
      <input
        type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
        disabled
        placeholder={`Enter ${field.label}`}
        className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-400 cursor-not-allowed"
      />
    </div>
  );
};

// ─── Table styles ─────────────────────────────────────────────────────────────

const customTableStyles = {
  headRow:   { style: { backgroundColor: "#f8fafc", borderBottomColor: "#e2e8f0", borderBottomWidth: "1px" } },
  headCells: { style: { fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", paddingLeft: "16px", paddingRight: "16px" } },
  cells:     { style: { fontSize: "13px", color: "#374151", paddingLeft: "16px", paddingRight: "16px", paddingTop: "8px", paddingBottom: "8px" } },
  rows:      { style: { borderBottomColor: "#f1f5f9" }, highlightOnHoverStyle: { backgroundColor: "#f8fafc", transitionDuration: "0.1s" } },
};

// ─────────────────────────────────────────────────────────────────────────────

export default function FormSetup() {
  // ── Data lists ──────────────────────────────────────────────────────────────
  const [tableDataItems,    setTableDataItems]    = useState([]);
  const [formTemplatesData, setFormTemplatesData] = useState([]);
  const [vendorsList,       setVendorsList]       = useState([]);

  // ── Assign modal state ───────────────────────────────────────────────────
  const [showAssign,         setShowAssign]         = useState(false);
  const [selectedVendor,     setSelectedVendor]     = useState("");
  const [selectedVendorData, setSelectedVendorData] = useState(null);
  const [usersList,          setUsersList]          = useState([]);
  const [selectedUser,       setSelectedUser]       = useState("");
  const [selectedFormType,   setSelectedFormType]   = useState("");
  const [formList,           setFormList]           = useState([]);
  const [selectedForm,       setSelectedForm]       = useState("");
  const [submitting,         setSubmitting]         = useState(false);
  const [showAssignPreview,  setShowAssignPreview]  = useState(false);

  // ── View modal state ──────────────────────────────────────────────────────
  const [viewOpen,    setViewOpen]    = useState(false);
  const [templateData,setTemplateData] = useState(null);

  // ── Filter state ──────────────────────────────────────────────────────────
  const [companyFilter, setCompanyFilter] = useState("");
  const [formFilter,    setFormFilter]    = useState("");
  const [userFilter,    setUserFilter]    = useState("");

  // ── RTK hooks ────────────────────────────────────────────────────────────
  const { data: vendorsData, error: vendorsError } =
    useFetchVendorsQuery(sessionStorage.getItem("access_token")) || {};

  const [fetchFormTemplates, { data: formTemplateData }] =
    useFetchFormTemplateMutation();

  const [fetchFormUsage, { data: FormUsageData }] =
    useFetchFormSetupMutation();

  const [addFormSetup, { data: FormSetupData, isLoading: FormSetupIsLoading, error: FormSetupError }] =
    useAddFormSetupMutation();

  const [deleteFormSetup, { data: deleteFormSetupData, error: deleteFormSetupError }] =
    useDeleteFormSetupMutation();

  // ── Bootstrap data ───────────────────────────────────────────────────────
  useEffect(() => {
    fetchFormUsage(sessionStorage.getItem("access_token"));
    fetchFormTemplates(sessionStorage.getItem("access_token"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (FormUsageData?.body) setTableDataItems(FormUsageData.body);
  }, [FormUsageData]);

  useEffect(() => {
    if (formTemplateData?.body) setFormTemplatesData(formTemplateData.body);
  }, [formTemplateData]);

  useEffect(() => {
    if (vendorsData?.body) setVendorsList(vendorsData.body);
    if (vendorsError)      toast.error("Failed to load vendors.");
  }, [vendorsData, vendorsError]);

  // ── Assign form result ───────────────────────────────────────────────────
  useEffect(() => {
    if (FormSetupData) {
      toast.success("Form successfully assigned.", { id: "formSetupSuccess" });
      setShowAssign(false);
      resetAssignForm();
      fetchFormUsage(sessionStorage.getItem("access_token"));
      setSubmitting(false);
    }
    if (FormSetupError) {
      toast.error(FormSetupError?.data?.message || "Assignment failed.", { id: "formSetupError" });
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [FormSetupData, FormSetupError]);

  // ── Delete result ────────────────────────────────────────────────────────
  useEffect(() => {
    if (deleteFormSetupData) {
      toast.success("Deleted successfully.");
      fetchFormUsage(sessionStorage.getItem("access_token"));
    }
    if (deleteFormSetupError) toast.error("Delete failed.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteFormSetupData, deleteFormSetupError]);

  // ── Assign modal helpers ──────────────────────────────────────────────────

  const resetAssignForm = () => {
    setSelectedVendor("");
    setSelectedVendorData(null);
    setUsersList([]);
    setSelectedUser("");
    setSelectedFormType("");
    setFormList([]);
    setSelectedForm("");
    setShowAssignPreview(false);
  };

  const handleVendorChange = (e) => {
    const id = e.target.value;
    setSelectedVendor(id);
    setSelectedUser("");
    const vendor = vendorsList.find(v => String(v.company_id) === String(id));
    setSelectedVendorData(vendor || null);
    setUsersList(vendor?.user_types || []);
  };

  const handleFormTypeChange = (e) => {
    const typeId = e.target.value;
    setSelectedFormType(typeId);
    setSelectedForm("");
    setShowAssignPreview(false);
    setFormList(formTemplatesData.filter(f => String(f.form_type) === String(typeId)));
  };

  const handleSubmit = () => {
    if (!selectedVendor)   { toast.error("Select a client.");    return; }
    if (!selectedUser)     { toast.error("Select a user type."); return; }
    if (!selectedFormType) { toast.error("Select a form type."); return; }
    if (!selectedForm)     { toast.error("Select a form.");      return; }

    const formData = formList.find(f => String(f.id) === String(selectedForm));
    if (!formData || !selectedVendorData) return;

    setSubmitting(true);
    addFormSetup({
      token: sessionStorage.getItem("access_token"),
      body: {
        tenant_id:          selectedVendorData.tenant_id,
        tenant_name:        selectedVendorData.name,
        industry_id:        formData.industry_id,
        industry_name:      formData.industry_name,
        form_template_id:   formData.id,
        form_template_name: formData.template_name,
        form_template_type: Number(selectedFormType),
        user_type:          selectedUser,
      },
    });
  };

  const handleDeleteWrapper = (r) => {
    toast(
      (t) => (
        <span>
          <p className="font-semibold text-gray-800">Delete this assignment?</p>
          <p className="text-xs text-gray-500 mt-0.5">
            "{r.form_template_name}" → {r.tenant_name}
          </p>
          <div className="flex gap-2 mt-3">
            <button
              className="px-3 py-1.5 text-xs rounded-lg bg-red-600 text-white hover:bg-red-700 font-medium"
              onClick={() => {
                deleteFormSetup({ token: sessionStorage.getItem("access_token"), id: r.id });
                toast.dismiss(t.id);
              }}
            >Delete</button>
            <button
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
              onClick={() => toast.dismiss(t.id)}
            >Cancel</button>
          </div>
        </span>
      ),
      { id: "deleteFormSetup", duration: Infinity }
    );
  };

  const openViewModal = (row) => {
    const tpl = formTemplatesData.find(f => f.id === row.form_template_id);
    if (tpl) { setTemplateData(tpl); setViewOpen(true); }
    else toast.error("Template data not available.");
  };

  // ── Filters ───────────────────────────────────────────────────────────────

  const filteredData = useMemo(() => {
    return (tableDataItems || []).filter(row => {
      const mc = !companyFilter || row.tenant_name?.toLowerCase().includes(companyFilter.toLowerCase());
      const mf = !formFilter    || row.form_template_name?.toLowerCase().includes(formFilter.toLowerCase());
      const mu = !userFilter    || String(row.user_type || "").toLowerCase().includes(userFilter.toLowerCase());
      return mc && mf && mu;
    });
  }, [tableDataItems, companyFilter, formFilter, userFilter]);

  const anyFilter = companyFilter || formFilter || userFilter;

  // ── Columns ───────────────────────────────────────────────────────────────

  const columns = [
    {
      name: "#",
      cell: (_, idx) => <span className="text-gray-400 font-mono text-xs">{idx + 1}</span>,
      width: "55px",
    },
    {
      name: "Client",
      selector: row => row.tenant_name,
      sortable: true,
      cell: row => (
        <div>
          <p className="font-semibold text-gray-800 text-sm">{row.tenant_name}</p>
          <p className="text-xs text-gray-400 font-mono">{row.tenant_id}</p>
        </div>
      ),
      grow: 1.5,
    },
    {
      name: "Industry",
      selector: row => row.industry_name,
      sortable: true,
      cell: row => <span className="text-sm text-gray-600">{row.industry_name || "—"}</span>,
    },
    {
      name: "Form",
      selector: row => row.form_template_name,
      sortable: true,
      cell: row => <span className="text-sm font-medium text-gray-800">{row.form_template_name}</span>,
      grow: 2,
    },
    {
      name: "Form Type",
      cell: row => (
        <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full border ${getTypeColor(row.form_template_type)}`}>
          {getTypeName(row.form_template_type)}
        </span>
      ),
    },
    {
      name: "User Type",
      selector: row => row.user_type,
      sortable: true,
      cell: row => (
        <span className="inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full border bg-gray-100 text-gray-700 border-gray-200">
          {row.user_type || "—"}
        </span>
      ),
    },
    {
      name: "Created",
      selector: row => row.created_at,
      sortable: true,
      cell: row => (
        <div>
          <p className="text-xs text-gray-600">{new Date(row.created_at).toLocaleDateString()}</p>
          <p className="text-xs text-gray-400">{row.created_by_name}</p>
        </div>
      ),
      width: "120px",
    },
    {
      name: "Actions",
      minWidth: "130px",
      ignoreRowClick: true,
      cell: row => (
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => openViewModal(row)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-medium transition-colors"
          >
            <FiEye className="h-3.5 w-3.5" /> View
          </button>
          <button
            onClick={() => handleDeleteWrapper(row)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-xs font-medium transition-colors"
          >
            <FiTrash2 className="h-3.5 w-3.5" /> Delete
          </button>
        </div>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="page-content">
      <div className="card p-0 rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

        {/* ── Page header ─────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h4 className="text-xl font-bold text-gray-900 mb-0">Form Setup</h4>
            <p className="text-sm text-gray-500 mt-0.5">Assign form templates to clients and user types.</p>
          </div>
          <button
            onClick={() => { resetAssignForm(); setShowAssign(true); }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm transition-all"
          >
            <FiPlus className="h-4 w-4" /> Assign Form
          </button>
        </div>

        {/* ── Filter bar ──────────────────────────────────────────────────── */}
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Client name…"
                value={companyFilter}
                onChange={e => setCompanyFilter(e.target.value)}
                className="block pl-9 pr-3 py-2 border border-gray-200 rounded-lg bg-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all w-48"
              />
            </div>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Form name…"
                value={formFilter}
                onChange={e => setFormFilter(e.target.value)}
                className="block pl-9 pr-3 py-2 border border-gray-200 rounded-lg bg-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all w-48"
              />
            </div>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="User type…"
                value={userFilter}
                onChange={e => setUserFilter(e.target.value)}
                className="block pl-9 pr-3 py-2 border border-gray-200 rounded-lg bg-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all w-40"
              />
            </div>
            {anyFilter && (
              <button
                onClick={() => { setCompanyFilter(""); setFormFilter(""); setUserFilter(""); }}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium text-gray-500 hover:text-gray-700 border border-gray-200 hover:border-gray-300 bg-white transition-all"
              >
                <FiX className="h-3 w-3" /> Clear
              </button>
            )}
          </div>
        </div>

        {/* ── Table ───────────────────────────────────────────────────────── */}
        <div className="px-2">
          <DataTable
            data={filteredData}
            columns={columns}
            customStyles={customTableStyles}
            pagination
            highlightOnHover
            pointerOnHover
            noDataComponent={
              <div className="flex flex-col items-center justify-center py-12 gap-2">
                <FiList className="h-12 w-12 text-gray-200" />
                <p className="text-sm font-semibold text-gray-400">No assignments found</p>
                <p className="text-xs text-gray-300">Use "Assign Form" to link a template to a client.</p>
              </div>
            }
          />
        </div>
      </div>

      {/* ── Assign Form Modal ──────────────────────────────────────────────── */}
      {showAssign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowAssign(false)} />
          <div className={`relative bg-white rounded-2xl shadow-2xl w-full flex flex-col overflow-hidden transition-all duration-300 ${showAssignPreview ? "max-w-4xl" : "max-w-lg"}`}>

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <FiLink className="h-4 w-4 text-blue-600" /> Assign Form to Client
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Link a form template to a tenant and user type.</p>
              </div>
              <button
                onClick={() => setShowAssign(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
              >
                <HiX className="h-4 w-4" />
              </button>
            </div>

            {/* Body — two panels when preview is open */}
            <div className="flex flex-1 min-h-0 overflow-hidden">

              {/* LEFT: form fields */}
              <div className="flex-shrink-0 w-full lg:w-[420px] flex flex-col">
                <div className="px-6 py-5 space-y-4 overflow-auto flex-1">

                  {/* Client */}
                  <div>
                    <label className={labelCls}>Client <span className="text-red-500">*</span></label>
                    <SelectField value={selectedVendor} onChange={handleVendorChange}>
                      <option value="">Select client…</option>
                      {vendorsList.map(v => (
                        <option key={v.company_id} value={v.company_id}>{v.name}</option>
                      ))}
                    </SelectField>
                  </div>

                  {/* User Type */}
                  <div>
                    <label className={labelCls}>User Type <span className="text-red-500">*</span></label>
                    <SelectField
                      value={selectedUser}
                      onChange={e => setSelectedUser(e.target.value)}
                      disabled={!selectedVendor}
                    >
                      <option value="">{selectedVendor ? "Select user type…" : "Select a client first"}</option>
                      {usersList.map((u, i) => (
                        <option key={i} value={u}>{u}</option>
                      ))}
                    </SelectField>
                    {selectedVendor && usersList.length === 0 && (
                      <p className="mt-1 text-xs text-amber-500">No user types configured for this client.</p>
                    )}
                  </div>

                  {/* Form Type */}
                  <div>
                    <label className={labelCls}>Form Type <span className="text-red-500">*</span></label>
                    <SelectField value={selectedFormType} onChange={handleFormTypeChange}>
                      <option value="">Select type…</option>
                      {FORM_TYPES.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </SelectField>
                  </div>

                  {/* Form + preview button */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className={`${labelCls} mb-0`}>Form <span className="text-red-500">*</span></label>
                      {selectedForm && (
                        <button
                          type="button"
                          onClick={() => setShowAssignPreview(p => !p)}
                          className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md transition-colors ${
                            showAssignPreview
                              ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                              : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          }`}
                        >
                          <FiEye className="h-3 w-3" />
                          {showAssignPreview ? "Hide preview" : "Preview form"}
                        </button>
                      )}
                    </div>
                    <SelectField
                      value={selectedForm}
                      onChange={e => { setSelectedForm(e.target.value); setShowAssignPreview(false); }}
                      disabled={!selectedFormType}
                    >
                      <option value="">{selectedFormType ? (formList.length ? "Select form…" : "No forms for this type") : "Select type first"}</option>
                      {formList.map(f => (
                        <option key={f.id} value={f.id}>{f.template_name}</option>
                      ))}
                    </SelectField>
                  </div>

                  {/* Summary pill */}
                  {selectedVendorData && selectedUser && selectedFormType && selectedForm && (() => {
                    const f = formList.find(x => String(x.id) === String(selectedForm));
                    return f ? (
                      <div className="rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
                        <span><span className="text-gray-400">Client:</span> <span className="font-semibold text-gray-700">{selectedVendorData.name}</span></span>
                        <span><span className="text-gray-400">User:</span> <span className="font-semibold text-gray-700">{selectedUser}</span></span>
                        <span><span className="text-gray-400">Type:</span> <span className="font-semibold text-gray-700">{getTypeName(selectedFormType)}</span></span>
                        <span><span className="text-gray-400">Form:</span> <span className="font-semibold text-gray-700">{f.template_name}</span></span>
                      </div>
                    ) : null;
                  })()}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
                  <button
                    onClick={() => setShowAssign(false)}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || FormSetupIsLoading}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold shadow-sm transition-all"
                  >
                    {(submitting || FormSetupIsLoading) && (
                      <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    )}
                    Assign Form
                  </button>
                </div>
              </div>

              {/* RIGHT: form preview panel */}
              {showAssignPreview && (() => {
                const previewForm = formList.find(x => String(x.id) === String(selectedForm));
                const fields = previewForm ? Object.values(previewForm.template || {}) : [];
                return (
                  <div className="flex-1 border-l border-gray-100 flex flex-col min-w-0 max-h-[80vh]">
                    <div className="px-5 py-3 border-b border-gray-100 flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-800">{previewForm?.template_name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{fields.length} field{fields.length !== 1 ? "s" : ""} · {previewForm?.language} · {previewForm?.industry_name}</p>
                    </div>
                    <div className="overflow-auto flex-1 p-4">
                      {fields.length === 0 ? (
                        <p className="text-sm text-center text-gray-400 py-8">No fields in this template.</p>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {fields.map((field, idx) => (
                            <FieldPreview key={idx} field={field} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── View Form Modal ────────────────────────────────────────────────── */}
      {viewOpen && templateData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setViewOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{templateData.template_name}</h3>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full border ${getTypeColor(templateData.form_type)}`}>
                    {getTypeName(templateData.form_type)}
                  </span>
                  <span className="text-xs text-gray-400">{templateData.industry_name}</span>
                  <span className="text-xs text-gray-300">·</span>
                  <span className="text-xs text-gray-400 capitalize">{templateData.language}</span>
                  <span className="text-xs text-gray-300">·</span>
                  <span className="text-xs text-gray-400">{Object.keys(templateData.template || {}).length} fields</span>
                </div>
              </div>
              <button
                onClick={() => setViewOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors flex-shrink-0 ml-4"
              >
                <HiX className="h-4 w-4" />
              </button>
            </div>
            <div className="overflow-auto flex-1 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.values(templateData.template).map((field, idx) => (
                  <FieldPreview key={idx} field={field} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
